import type { DiaClient } from './client';
import { cacheRows, cachedRows, browserOffline } from './queries';
import { pendingOps } from './outbox';

/**
 * Water, read by hand.
 *
 * The tank sensors went back, so the only honest water number is the one a
 * person reads off a sight gauge, in quarters (0 empty, 4 full). Each entry is
 * a row in water_levels (migration 027), queued through the outbox like every
 * other write, so a level entered with no signal is kept and sent later.
 *
 * Nothing here estimates. The newest entry per tank is shown with its age; a
 * tank nobody has entered is "no entry", never a guess; and an entry more than
 * a day old is greyed by the screen (WATER_STALE_HOURS).
 */

export const TANKS = ['hacienda', 'bungalow', 'pavilion', 'farm', 'casita'] as const;
export type Tank = (typeof TANKS)[number];

export type Quarters = 0 | 1 | 2 | 3 | 4;

/** An entry older than this is greyed on the screen. */
export const WATER_STALE_HOURS = 24;

export type WaterLevel = {
  id: string;
  client_id: string;
  tank: Tank;
  quarters: Quarters;
  read_at: string;
  entered_by: string | null;
  created_at: string;
};

/** An entry as the screen needs it: the server's rows and the ones still waiting in the outbox. */
export type WaterEntry = {
  id: string;
  tank: Tank;
  quarters: Quarters;
  readAt: string;
  /** True while the entry is only on this phone. */
  pending: boolean;
};

export type WaterNow = { quarters: Quarters; readAt: string; pending: boolean; ageHours: number };

export function isTank(v: unknown): v is Tank {
  return typeof v === 'string' && (TANKS as readonly string[]).includes(v);
}

export function isQuarters(v: unknown): v is Quarters {
  return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= 4;
}

/**
 * Rows from the server, with the time the island received each one as a ceiling
 * on the time the phone said: a phone whose clock runs fast cannot make an
 * entry look newer than the moment it arrived.
 */
function fromServer(rows: WaterLevel[]): WaterEntry[] {
  return rows
    .filter((r) => isTank(r.tank) && isQuarters(r.quarters))
    .map((r) => {
      const said = Date.parse(r.read_at);
      const got = Date.parse(r.created_at);
      const at = Number.isFinite(said) ? (Number.isFinite(got) ? Math.min(said, got) : said) : got;
      return { id: r.id, tank: r.tank, quarters: r.quarters, readAt: new Date(at).toISOString(), pending: false };
    });
}

/** Entries made on this phone that the island has not confirmed yet. */
export async function pendingWater(nowMs = Date.now()): Promise<WaterEntry[]> {
  const out: WaterEntry[] = [];
  for (const op of await pendingOps()) {
    if (op.table !== 'water_levels' || op.kind !== 'insert') continue;
    const tank = op.payload.tank;
    const quarters = op.payload.quarters;
    if (!isTank(tank) || !isQuarters(quarters)) continue;
    const said = Date.parse(String(op.payload.read_at ?? ''));
    // Nothing counts from the future, so a phone with a fast clock cannot pin a level.
    const at = Math.min(Number.isFinite(said) ? said : op.createdAt, nowMs);
    out.push({ id: op.id, tank, quarters, readAt: new Date(at).toISOString(), pending: true });
  }
  return out;
}

/** The recent entries, from the island when it answers and from this phone's copy when it does not. */
export async function loadWater(client: DiaClient, nowMs = Date.now()): Promise<WaterEntry[]> {
  let rows: WaterLevel[] | null = null;
  if (!browserOffline()) {
    try {
      const { data, error } = await client
        .from('water_levels')
        .select('*')
        .order('read_at', { ascending: false })
        .limit(200);
      if (error) throw new Error(error.message);
      if (Array.isArray(data)) rows = await cacheRows('water_levels', data as WaterLevel[]);
    } catch {
      // Offline is normal here. Answer from what the phone already has.
    }
  }
  if (!rows) rows = await cachedRows<WaterLevel>('water_levels');
  return [...fromServer(rows), ...(await pendingWater(nowMs))];
}

/** The newest entry for each tank, with its age. A tank nobody has entered is null. */
export function latestWater(entries: WaterEntry[], nowMs: number): Record<Tank, WaterNow | null> {
  const out = Object.fromEntries(TANKS.map((t) => [t, null])) as Record<Tank, WaterNow | null>;
  for (const e of entries) {
    const at = Date.parse(e.readAt);
    if (!Number.isFinite(at)) continue;
    const have = out[e.tank];
    if (have && Date.parse(have.readAt) >= at) continue;
    out[e.tank] = {
      quarters: e.quarters,
      readAt: e.readAt,
      pending: e.pending,
      ageHours: Math.max(0, (nowMs - at) / 3_600_000),
    };
  }
  return out;
}

/** The rows to queue for one sitting of "enter levels". Tanks left blank are not written. */
export function waterPayloads(
  levels: Partial<Record<Tank, number | null>>,
  readAt: string,
  enteredBy: string | null,
): Array<{ tank: Tank; quarters: Quarters; read_at: string; entered_by: string | null }> {
  const rows: Array<{ tank: Tank; quarters: Quarters; read_at: string; entered_by: string | null }> = [];
  for (const tank of TANKS) {
    const q = levels[tank];
    if (q === null || q === undefined || !isQuarters(q)) continue;
    rows.push({ tank, quarters: q, read_at: readAt, entered_by: enteredBy });
  }
  return rows;
}
